<template>
  <div :class="{ 'sliding-list--overflown': !shouldDisableSwiping }" class="layout d-flex flex-column">
    <horizontal-sliding-list-indicator-dots
      v-if="showIndicatorDots && !itemsFitInContainer && swiper"
      :slides="swiper.slides"
      :active-index="swiper.activeIndex"
      class="mb-4"
    />
    <div class="flex flex-wrapper">
      <div ref="container" class="swiper-container" :class="{ 'px-4': $vuetify.display.mobile && !noMobilePadding }">
        <div
          ref="wrapper"
          class="swiper-wrapper"
          :class="{ 'desktop-padding': !$vuetify.display.mobile && desktopPadding }"
          @wheel="(e) => (shouldDisableSwiping ? {} : e.preventDefault())"
        >
          <slot />
        </div>
      </div>
      <template v-if="!$vuetify.display.mobile">
        <the-button
          v-if="showPrev"
          class="swiper-button-prev-custom px-0"
          :class="{ 'swiper-button-prev-custom--inner': navigationButtonsInside }"
          custom-colors
          no-hover
          :ripple="false"
          shadow
          @click="prev"
        >
          <i-custom-arrow-left class="size-2" />
        </the-button>

        <the-button
          v-if="showNext"
          class="swiper-button-next-custom px-0"
          :class="{ 'swiper-button-next-custom--inner': navigationButtonsInside }"
          custom-colors
          no-hover
          :ripple="false"
          shadow
          @click="next"
        >
          <i-custom-arrow-right class="size-2" />
        </the-button>
      </template>
    </div>
  </div>
</template>

<script>
import Swiper from "swiper"

import { Mousewheel, Navigation, FreeMode } from "swiper/modules"

import HorizontalSlidingListIndicatorDots from "@/components/HorizontalSlidingListIndicatorDots.vue"

Swiper.use([Navigation, Mousewheel, FreeMode])

export default {
  components: {
    HorizontalSlidingListIndicatorDots
  },
  props: {
    swipingDisabled: {
      type: Boolean,
      default: false
    },
    navigationButtonsInside: {
      type: Boolean,
      default: false
    },
    spaceBetweenItems: {
      type: Number,
      default: 16
    },
    step: {
      type: Number,
      default: 4
    },
    slidesOffset: {
      type: Number,
      default: 0
    },
    showIndicatorDots: {
      type: Boolean,
      default: false
    },
    initialSlideIndex: {
      type: Number,
      default: 0
    },
    noMobilePadding: {
      type: Boolean,
      default: false
    },
    desktopPadding: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      swiper: null,
      itemsFitInContainer: null
    }
  },
  computed: {
    showPrev() {
      return this.swiper && !this.swiper.isBeginning && !this.shouldDisableSwiping
    },
    showNext() {
      return this.swiper && !this.swiper.isEnd && !this.shouldDisableSwiping
    },
    shouldDisableSwiping() {
      return this.itemsFitInContainer || this.swipingDisabled
    }
  },
  watch: {
    slidesOffset() {
      if (this.swiper) {
        this.swiper.params.slidesOffsetBefore = this.slidesOffset
        this.swiper.params.slidesOffsetAfter = this.slidesOffset
        this.swiper.update()
      }
    },
    spaceBetweenItems() {
      if (this.swiper) {
        this.swiper.params.spaceBetween = this.spaceBetweenItems
        this.swiper.update()
      }
    }
  },
  activated() {
    this.$eventBus.$on("window-resize", this.setSwipingAndSetupSwiper)
    // When the list is a cached component and it gets rerendered, swiper doesn't setup correctly
    this.setSwipingAndSetupSwiper()
    if (this.swiper) {
      this.swiper.update()
    }
  },
  deactivated() {
    this.$eventBus.$off("window-resize", this.setSwipingAndSetupSwiper)
  },
  mounted() {
    this.$eventBus.$on("window-resize", this.setSwipingAndSetupSwiper)
    this.setSwipingAndSetupSwiper()
  },
  beforeUnmount() {
    this.$eventBus.$off("window-resize", this.setSwipingAndSetupSwiper)
    if (this.swiper) {
      this.swiper.destroy(true, false)
    }
  },
  methods: {
    setupSwiper() {
      if (this.swiper) {
        this.swiper.destroy(true, false)
      }

      //Use observer true for both cases in case some slides are added/removed
      const options = this.shouldDisableSwiping
        ? {
            spaceBetween: this.spaceBetweenItems,
            slidesPerView: "auto",
            allowTouchMove: false,
            initialSlide: this.initialSlideIndex,
            observer: true
          }
        : {
            init: false,
            slidesPerView: "auto",
            observer: true,
            mousewheel: true,
            touchRatio: 1,
            freeMode: {
              enabled: true,
              sticky: true,
              momentumRatio: 0.6,
              velocityRatio: 0.6
            },
            spaceBetween: this.spaceBetweenItems,
            slidesOffsetBefore: this.slidesOffset,
            slidesOffsetAfter: this.slidesOffset,
            threshold: 10,
            initialSlide: this.initialSlideIndex
          }

      this.swiper = new Swiper(this.$refs.container, options)
      this.swiper.on("slideChange", (newVal) => {
        this.$emit("active-index-change", newVal.activeIndex)
      })
      this.swiper.init()
    },
    next() {
      this.swiper.slideTo(this.swiper.activeIndex + this.step)
    },
    prev() {
      this.swiper.slideTo(this.swiper.activeIndex - this.step)
    },
    slideToItem(index) {
      this.swiper.slideTo(index)
    },
    async setSwipingAndSetupSwiper() {
      await this.$nextTick()

      const containerWidth = this.$refs.container?.getBoundingClientRect()?.width
      const widthOfAllItems = this.$refs.wrapper?.getBoundingClientRect()?.width

      if (!containerWidth && !widthOfAllItems) {
        return
      }

      const margins = this.slidesOffset || (this.$vuetify.display.mobile ? 32 : 0)
      const itemsFitInContainer = containerWidth - margins >= widthOfAllItems

      if (itemsFitInContainer !== this.itemsFitInContainer) {
        this.itemsFitInContainer = itemsFitInContainer
        this.setupSwiper()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.layout {
  .flex {
    position: relative;
    max-width: 100%;
  }

  .swiper-container {
    width: 100%;
    overflow: hidden;
    position: relative;
    text-align: left;

    .swiper-wrapper {
      height: auto;
      width: auto;
      display: inline-flex;
      word-break: break-word;

      &.desktop-padding {
        padding-left: 2px;
      }
    }
  }

  .swiper-button-next-custom,
  .swiper-button-prev-custom {
    position: absolute;
    border: none !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    height: 24px !important;
    width: 24px !important;
    min-width: 24px !important;
    border-radius: var(--rounded-circle) !important;
    z-index: var(--z-index-base) !important;
    cursor: pointer !important;
    box-shadow: var(--shadow-1) !important;
    color: var(--post-tint) !important;
    background-color: var(--post-background) !important;
  }

  .swiper-button-next-custom {
    right: -12px;

    &--inner {
      right: 0;
    }
  }

  .swiper-button-prev-custom {
    left: -12px;

    &--inner {
      left: 0;
    }
  }

  .swiper-button-disabled {
    display: none;
  }
}
</style>
